import React from "react";

import { useTranslation } from "utils";

import { PRACTICE_SLUG, PracticeSlug } from "models";

import { Button, Flex, Icon, Tooltip, Typography } from "components";

export type MissingDataTypes = PracticeSlug | "YIELD";

export type MultiCropSelectionBarMissingDataProps = {
  selectedCount: number;
  onEdit: (missingDataTypes: MissingDataTypes) => void;
  onClear: () => void;
  addSeedingEnabled: boolean;
  addTillageEnabled: boolean;
  addOrganicFertilizerEnabled: boolean;
  addGrowingPeriodEnabled: boolean;
  // the Add yield and Add mineral fertilizer buttons only exist in the all-fields-data-table-view usage of this
  // bar, so their visibility is driven by these flags rather than by whether the enabled flags happen to be passed
  showAddYieldButton?: boolean;
  addYieldEnabled?: boolean;
  showAddMineralFertilizerButton?: boolean;
  addMineralFertilizerEnabled?: boolean;
  hidePracticeButtons?: boolean;
};

export const MultiCropSelectionBarMissingData = ({
  selectedCount,
  onEdit,
  onClear,
  addTillageEnabled,
  addSeedingEnabled,
  addOrganicFertilizerEnabled,
  addGrowingPeriodEnabled,
  showAddYieldButton,
  addYieldEnabled,
  showAddMineralFertilizerButton,
  addMineralFertilizerEnabled,
  hidePracticeButtons,
}: MultiCropSelectionBarMissingDataProps) => {
  const { t } = useTranslation(["historicalPracticeDataTableView"]);

  return (
    <Flex
      data-testid="multi-crop-selection-bar-missing-data"
      className="multi-crop-selection-bar-missing-data"
      align="center"
      justify="space-between"
      gap="3rem"
    >
      <Flex align="center" gap={"0.5rem"}>
        <Typography variant="paragraph-m" className="selection-count no-wrap">
          {t("multiSelectBar.selectedItems", { count: selectedCount })}
        </Typography>
        <Tooltip placement="top" title={t("multiSelectBar.clear")}>
          <Icon className="selection-bar-icon" iconName="tabler/x" color="white" size="s" onClick={onClear} />
        </Tooltip>
      </Flex>
      <Flex align="center" gap={"0.75rem"}>
        <Button
          dataTestId="multi-crop-selection-bar-missing-data-add-growing-period"
          type="light primary"
          onClick={() => onEdit(PRACTICE_SLUG.CROPS)}
          disabled={!addGrowingPeriodEnabled}
        >
          {t("multiSelectBar.addGrowingPeriod")}
        </Button>
        {!hidePracticeButtons && (
          <>
            <Button
              dataTestId="multi-crop-selection-bar-missing-data-add-seeding"
              type="light primary"
              onClick={() => onEdit(PRACTICE_SLUG.SEEDINGS)}
              disabled={!addSeedingEnabled}
            >
              {t("multiSelectBar.addSeeding")}
            </Button>
            <Button
              dataTestId="multi-crop-selection-bar-missing-data-add-tillage"
              type="light primary"
              onClick={() => onEdit(PRACTICE_SLUG.TILLAGES)}
              disabled={!addTillageEnabled}
            >
              {t("multiSelectBar.addTillage")}
            </Button>
            <Button
              dataTestId="multi-crop-selection-bar-missing-data-add-organic-fertilizer"
              type="light primary"
              onClick={() => onEdit(PRACTICE_SLUG.ORGANIC_FERTILIZERS)}
              disabled={!addOrganicFertilizerEnabled}
            >
              {t("multiSelectBar.addOrganicFertilizer")}
            </Button>
            {showAddMineralFertilizerButton && (
              <Button
                dataTestId="multi-crop-selection-bar-missing-data-add-mineral-fertilizer"
                type="light primary"
                onClick={() => onEdit(PRACTICE_SLUG.MINERAL_FERTILIZERS)}
                disabled={!addMineralFertilizerEnabled}
              >
                {t("multiSelectBar.addMineralFertilizer")}
              </Button>
            )}
            {showAddYieldButton && (
              <Button
                dataTestId="multi-crop-selection-bar-missing-data-add-yield"
                type="light primary"
                onClick={() => onEdit("YIELD")}
                disabled={!addYieldEnabled}
              >
                {t("multiSelectBar.addYield")}
              </Button>
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
};
